import {
  CheckIcon,
  ChevronRightIcon,
  Clock8Icon,
  LoaderCircleIcon,
  XIcon,
} from 'lucide-react'
import { ReactNode } from 'react'

import { TransactionStatus } from '@/contexts/TransactionProvider'
import { useWeb3Context } from '@/contexts/Web3Provider'
import { cn } from '@/theme/utils'
import { UiCard } from '@/ui/cards/UiCard'

export interface TransactionStatusIndicatorProps {
  status: TransactionStatus
  title?: string
  message?: string
  hash?: string
  onClose?: () => void
  className?: string
  compact?: boolean
}

/**
 * A reusable component for displaying transaction status.
 * Can be used in both compact mode (for inline display) or full mode (with title and message).
 */
export default function TransactionStatusIndicator({
  status,
  title,
  message,
  hash,
  onClose,
  className,
  compact = false,
}: TransactionStatusIndicatorProps) {
  const { client } = useWeb3Context()

  // Status icon mapping
  const statusIcon: Record<TransactionStatus, ReactNode> = {
    pending: (
      <Clock8Icon
        className={cn(
          'aspect-square text-yellow-500',
          compact ? 'size-3.5' : 'size-6',
        )}
      />
    ),
    processing: (
      <LoaderCircleIcon
        className={cn(
          'aspect-square animate-spin text-blue-500',
          compact ? 'size-3.5' : 'size-6',
        )}
      />
    ),
    success: (
      <CheckIcon
        className={cn(
          'aspect-square text-green-500',
          compact ? 'size-3.5' : 'size-6',
        )}
      />
    ),
    failed: (
      <XIcon
        className={cn(
          'aspect-square text-red-500',
          compact ? 'size-3.5' : 'size-6',
        )}
      />
    ),
  }

  // Status text mapping
  const statusTitle: Record<TransactionStatus, string> = {
    pending: title || 'Transaction Pending',
    processing: title || 'Processing Transaction',
    success: title || 'Transaction Successful',
    failed: title || 'Transaction Failed',
  }

  // Default status messages
  const statusMessage: Record<TransactionStatus, string> = {
    pending: message || 'Your transaction is waiting to be mined',
    processing: message || 'Your transaction is being processed',
    success: message || 'Your transaction has been confirmed',
    failed: message || 'Your transaction has failed. Please try again',
  }

  // Background color based on status
  const getBgColor = () => {
    switch (status) {
      case 'success':
        return 'bg-green-500/10 border-green-500/20'
      case 'failed':
        return 'bg-red-500/10 border-red-500/20'
      case 'pending':
        return 'bg-yellow-500/10 border-yellow-500/20'
      case 'processing':
        return 'bg-blue-500/10 border-blue-500/20'
      default:
        return ''
    }
  }

  // Render compact version
  if (compact) {
    return (
      <div
        className={cn(
          'flex items-center gap-1 rounded-md border-1 px-1 py-0.5 pr-1.5',
          getBgColor(),
          className,
        )}
      >
        {statusIcon[status]}
        <span className='text-foreground text-sm font-medium'>
          {status === 'processing'
            ? 'Processing...'
            : status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>
    )
  }

  // Render full version
  return (
    <UiCard
      className={cn('relative overflow-hidden p-4', getBgColor(), className)}
    >
      <div className='flex items-start gap-2'>
        <div className='shrink-0'>{statusIcon[status]}</div>
        <div className='flex-1'>
          <h4 className='text-foreground font-medium'>{statusTitle[status]}</h4>
          <p className='text-muted-foreground text-sm'>
            {statusMessage[status]}
          </p>

          {hash && (
            <a
              href={`${client?.chain.blockExplorers?.default.url}/tx/${hash}`}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:text-primary/80 typography-m3-body-small mt-1'
            >
              View on Explorer
              <ChevronRightIcon className='ml-1 inline aspect-square size-4' />
            </a>
          )}
        </div>

        {onClose && (
          <button
            onClick={onClose}
            className='text-muted-foreground hover:text-foreground absolute top-2 right-2'
          >
            <XIcon className='h-4 w-4' />
          </button>
        )}
      </div>
    </UiCard>
  )
}
